import {
  Button,
  Checkbox,
  Flex,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import withErrorBoundaryAndPolicyCheck from '@v2/commons/HOC/ErrorBoundaryAndPolicyCheckHOC'
import ModalSelect from '@v2/commons/components/ModalSelect/modalSelect.component'
import { POLICIES } from '@v2/commons/config'
import { TASK_PRIORITY_OPTIONS } from '@v2/domains/task/task.types'
import useGetUsersNoPaginattionUseCase from '@v2/domains/user/hooks/useGetUsersNoPagination.useCase'
import { useEffect, useState } from 'react'

import { Icons } from 'src/common/components/ui'

import {
  ESCALATION_ISSUE_OPTIONS,
  ESCALATION_PROGRAM_OPTIONS,
  ESCALATION_SERVICE_OPTIONS,
  ESCALATION_STATUS_OPTIONS,
  EscalationIssueEnum,
  EscalationProgramEnum,
} from '../../escalation.types'
import useAssignEscalation from '../../hooks/useAssignEscalation.hook'
import { openUpdateEscalationModal } from '../../views/UpdateEscalationModal.view'
import styles from './escalation-modal.module.scss'

export interface ModalProps {
  selectedItem: string
  driverLicense?: string
  driverName?: string
  driverEmail?: string
  driverPhoneNumber?: string
  clearStore: () => void
  openSuccessAssignEscalationModal: () => void
}
enum Submitter {
  SAVE = 'save',
  CONTINUE = 'continue',
}
export const TOOLTIPS = {
  issue: 'Please choose a program first to see related issues',
  service: 'Please choose an issue first to see related services',
}

const AssignEscalationkModal = ({
  selectedItem,
  clearStore,
  openSuccessAssignEscalationModal,
  driverLicense,
  driverName,
  driverEmail,
  driverPhoneNumber,
}: ModalProps) => {
  const {
    onSubmitForm,
    escalationMutationStatus,
    createEscalationForm,
    hasUpdtaeEscalationPolicy,
    hasViewEscalationPolicy,
  } = useAssignEscalation()
  const { assignees } = useGetUsersNoPaginattionUseCase()

  const [issue, setIssue] = useState<string | null | undefined>(null)
  const [service, setService] = useState<string | null | undefined>(null)

  const [filteredIssues, setFilteredIssues] = useState<
    typeof ESCALATION_ISSUE_OPTIONS
  >([])
  const [filteredServices, setFilteredServices] = useState<
    typeof ESCALATION_SERVICE_OPTIONS
  >([])

  const isProgramSelected = !!createEscalationForm.getValues().program

  createEscalationForm.watch('program', ({ value }) => {
    onSetIssueByProgram(value!)
  })

  createEscalationForm.watch('issue', ({ value }) => {
    setIssue(value)
    onSetServiceByIssue(value!)
  })

  createEscalationForm.watch('service', ({ value }) => {
    setService(value)
  })

  const onSetIssueByProgram = (value: EscalationProgramEnum) => {
    const filteredIssues = ESCALATION_ISSUE_OPTIONS.filter(
      (option) => !option.program || option.program === value!,
    )
    setFilteredIssues(filteredIssues)
    setIssue(null)
    setService(null)
  }

  const onSetServiceByIssue = (value: EscalationIssueEnum) => {
    const filteredServices = ESCALATION_SERVICE_OPTIONS.filter(
      (option) => option.issue === value!,
    )
    setFilteredServices(filteredServices)
    setService(null)
  }

  useEffect(() => {
    if (escalationMutationStatus.isSuccess) {
      clearStore()
    }
  }, [escalationMutationStatus.isSuccess])

  const getOnSuccessSubmitCb = (submitter: Submitter) => (taskId: string) => {
    modals.closeAll()
    if (submitter === Submitter.SAVE) {
      openSuccessAssignEscalationModal()
    } else {
      openUpdateEscalationModal({
        selectedItem: taskId,
        drivingLicense: driverLicense,
        driverPhoneNumber,
      })
    }
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const { submitter } = event.nativeEvent as SubmitEvent
    const onSuccessCb = getOnSuccessSubmitCb(submitter!.id as Submitter)

    onSubmitForm(selectedItem, onSuccessCb)
  }

  return (
    <Paper className={styles['modal-body']}>
      <Text className={styles['modal-header']} ta="center">
        Create New Escalation
      </Text>
      <form
        onSubmit={(e) => {
          handleSubmit(e)
        }}
      >
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          <TextInput
            label="Driver's Name"
            value={driverName}
            classNames={{
              input: styles['record-info-input'],
              label: styles['record-info-label'],
            }}
            readOnly
          />
          <TextInput
            label="Driver's TLC/DL"
            value={driverLicense}
            classNames={{
              input: styles['record-info-input'],
              label: styles['record-info-label'],
            }}
            readOnly
          />
          <TextInput
            label="Driver's Email"
            value={driverEmail}
            classNames={{
              input: styles['record-info-input'],
              label: styles['record-info-label'],
            }}
            readOnly
          />
          <TextInput
            label="Driver's Phone Number"
            value={driverPhoneNumber}
            classNames={{
              input: styles['record-info-input'],
              label: styles['record-info-label'],
            }}
            readOnly
          />
          <ModalSelect
            label="Program"
            placeholder="Select a Program"
            options={ESCALATION_PROGRAM_OPTIONS}
            {...createEscalationForm.getInputProps('program')}
            key={createEscalationForm.key('program')}
          />
          <Tooltip
            position="bottom"
            display={
              createEscalationForm.getValues().program ? 'none' : 'block'
            }
            label={TOOLTIPS.issue}
          >
            <Paper>
              <ModalSelect
                label="Issue"
                placeholder="Select an Issue"
                value={issue}
                {...createEscalationForm.getInputProps('issue')}
                options={filteredIssues}
                key={createEscalationForm.key('issue')}
                showArrowIcon={isProgramSelected}
                readOnly={!isProgramSelected}
              />
            </Paper>
          </Tooltip>
          <Tooltip
            position="bottom"
            display={issue ? 'none' : 'block'}
            label={TOOLTIPS.service}
          >
            <Paper>
              <ModalSelect
                label="Benefit or Service (if applicable)"
                placeholder="Select a benefit or service"
                {...createEscalationForm.getInputProps('service')}
                options={filteredServices}
                key={createEscalationForm.key('service')}
                value={service}
                showArrowIcon={!!issue && filteredServices.length > 0}
              />
            </Paper>
          </Tooltip>
          <ModalSelect
            required={false}
            label="Priority"
            placeholder="Select Priority"
            {...createEscalationForm.getInputProps('priority')}
            key={createEscalationForm.key('priority')}
            options={TASK_PRIORITY_OPTIONS}
            allowDeselect={false}
          />
          <ModalSelect
            label="Assign To"
            placeholder="Select Assignee"
            {...createEscalationForm.getInputProps('assignedToId')}
            options={assignees ?? []}
            key={createEscalationForm.key('assignedToId')}
            searchable
            nothingFoundMessage="No users found"
          />

          <ModalSelect
            {...createEscalationForm.getInputProps('status')}
            defaultValue="New"
            key={createEscalationForm.key('status')}
            label="Status"
            options={ESCALATION_STATUS_OPTIONS}
            readOnly
            required={false}
            showArrowIcon={false}
            placeholder="New"
          />
          <Stack style={{ alignSelf: 'center' }}>
            <Checkbox
              label="Notify Assignee via Email"
              radius="3px"
              {...createEscalationForm.getInputProps('isNotify')}
              key={createEscalationForm.key('isNotify')}
              mt={7}
            />
          </Stack>
        </SimpleGrid>
        <Flex justify="space-between" mt="30px">
          <Button
            variant="default"
            className={styles['button-cancel']}
            disabled={escalationMutationStatus.isLoading}
            type="submit"
            id={Submitter.SAVE}
          >
            Save & Complete Later
          </Button>
          {hasUpdtaeEscalationPolicy && (
            <Button
              rightSection={<Icons.ArrowRight />}
              className={styles['button-save']}
              type="submit"
              id={Submitter.CONTINUE}
              disabled={escalationMutationStatus.isLoading}
              loading={escalationMutationStatus.isLoading}
            >
              Continue to Part 2
            </Button>
          )}
        </Flex>
      </form>
    </Paper>
  )
}

export default withErrorBoundaryAndPolicyCheck(
  AssignEscalationkModal,
  POLICIES.isCreateEscalationEnabled,
)
